import { useQuery } from "@apollo/client";
import classNames from "classnames";
import { FC, MouseEvent, useEffect, useMemo } from "react";
import Divider from "~/components/Divider";
import { PATH } from "~/constants/enum";
import { QUERY_GET_ACTUAL_PRODUCT } from "~/data/home";
import { Product } from "~/services/product";
import { getConfig, getUrlCustom, isUsingLayoutV2 } from "~/utils/theme";
import { transformProductItem } from "~/utils/transform";
import ProductImage, { Props as ProductImageProps } from "./ProductImage";
import styles from "./ProductItem.module.scss";
import ProductName, { Props as ProductNameProps } from "./ProductName";
import ProductPrice from "./ProductPrice";
import { isEmpty } from "lodash";
import { QUERY_PRODUCT_INDEX } from "~/data/product/gql";

export type ProductItemSize =
  | "large"
  | "medium"
  | "small"
  | "xsmall"
  | "smaller";
export enum DisplayInfo {
  Price = "price",
  Rating = "rating",
  Wishlist = "wishlist",
  Compare = "compare",
  QuickView = "quickView",
  AddToCart = "addToCart",
  Title = "title",
}
export interface ProductItemProps {
  size?: ProductItemSize;
  data: Partial<Product>;
  search?: string;
  onProductClick?: (product: Partial<Product>) => void;
  params?: any;
  loading?: boolean;
  className?: string;
  rootClassName?: string;
  onRemove?: (data: Partial<Product>) => void;
  actions?: (data: Partial<Product>) => JSX.Element;
  type?: "vertical" | "horizontal";
  imageProps?: Partial<ProductImageProps>;
  nameProps?: Partial<ProductNameProps>;
  onCompleted?: (data: Product, clientOptions: any) => void;
  displayInfo?: DisplayInfo[] | any[];
}

const ProductItem: FC<ProductItemProps> = ({
  data: optimizeData,
  search,
  onProductClick,
  onRemove,
  params = {},
  loading: productItemLoading,
  className = "",
  actions,
  type = "vertical",
  imageProps,
  nameProps,
  onCompleted,
  displayInfo = [DisplayInfo.Title, DisplayInfo.Price],
}) => {
  const themeConfig = getConfig("ProductItem");

  const { data: actualData, loading } = useQuery(QUERY_GET_ACTUAL_PRODUCT, {
    // nextFetchPolicy: "no-cache",
    onError: ({ message }) => {
      console.error(optimizeData.sku);
    },
    onCompleted(data) {
      onCompleted?.(data?.products?.items?.[0], {
        variables: { sku: optimizeData.sku },
      });
    },
    context: { fetchOptions: { method: "GET" } },
    variables: {
      sku: optimizeData.sku,
    },
    skip:
      !optimizeData?.sku || !isEmpty(optimizeData?.price_range?.minimum_price),
  });

  const productActual = transformProductItem(
    actualData?.products.items?.[0] || ({} as any),
  );

  const data: Partial<Product> = useMemo(() => {
    if (!optimizeData) return {};
    return {
      ...(optimizeData || {}),
      ...(productActual || {}),
      images: !!optimizeData?.images?.length
        ? optimizeData?.images
        : !!optimizeData?.media_gallery?.length
          ? optimizeData?.media_gallery?.map((item: any) => item.url)
          : !!productActual?.media_gallery?.length
            ? productActual?.media_gallery?.map((item: any) => item.url)
            : [optimizeData?.image?.url],
      tracking: productActual?.tracking || optimizeData?.tracking,
      available_in_stock:
        productActual?.available_in_stock || optimizeData?.available_in_stock,
    };
  }, [optimizeData, productActual]);

  const productIndex: {
    last_24h_view_count: number;
    last_24h_sale_count: number;
    last_24h_in_cart_count: number;
  } = useMemo(() => {
    return (
      data?.tracking || {
        last_24h_view_count: 0,
        last_24h_sale_count: 0,
        last_24h_in_cart_count: 0,
      }
    );
  }, [data?.tracking]);

  const skeletonLoading =
    productItemLoading ||
    (isEmpty(optimizeData?.price_range?.minimum_price) && loading);

  const href =
    data.url_key &&
    // !loading &&
    getUrlCustom({
      pathname: PATH.ProductDetail,
      query: {
        slug: data.url_key,
        variant: data.variants?.[0]?.product?.id,
      },
    });

  const handleRemove = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();

    onRemove?.(data);
    // setIsFavorite(!isFavorite);
  };
  const handleAction = (e: MouseEvent<HTMLDivElement>) => {
    actions?.(data);
  };
  const handleClickProduct = () => {
    if (!data.sku || !data.slug) return;
    localStorage.setItem(`${data.sku}`, JSON.stringify(params));
    onProductClick?.(data);
  };

  // useEffect(() => {
  //     if (optimizeData.sku && !!called && !actualData?.products?.items?.length && !skeletonLoading) {
  //     }
  // }, [optimizeData.sku && !!called && !actualData?.products?.items?.length && !skeletonLoading]);

  // if (!!optimizeData.sku && !skeletonLoading) {
  //     return null;
  // }

  return (
    <div
      className={classNames(
        "flex flex-col product-item gap-2 h-full",
        styles.product_item,
        className,
        {
          ["!flex-row"]: type === "horizontal",
        },
        {
          "!border-none !p-0": isUsingLayoutV2,
        },
      )}
      key={data.slug}
      data-sku={data.sku}
      onClick={handleClickProduct}
    >
      <div
        className={classNames(
          styles.product_item_hover,
          " relative aspect-square h-full",
          {
            "max-w-40 lg:max-w-[50%] flex-1": type === "horizontal",
          },
          imageProps?.className || "",
        )}
      >
        <ProductImage
          href={href || ""}
          data={data as Product}
          loading={skeletonLoading}
          onRemove={onRemove && handleRemove}
          isBestSeller={!!productIndex?.last_24h_sale_count}
          {...(imageProps || {})}
        />
      </div>
      <div
        className={classNames({
          "flex-1": type === "horizontal",
          hidden:
            !displayInfo.includes(DisplayInfo.Price) &&
            !displayInfo.includes(DisplayInfo.Title),
          "flex flex-col items-center": isUsingLayoutV2,
        })}
      >
        <ProductName
          href={href || ""}
          loading={skeletonLoading}
          name={data?.name}
          search={search}
          hidden={!displayInfo.includes(DisplayInfo.Title)}
          type={type}
          {...(nameProps || {})}
        />
        {themeConfig.showDivider && <Divider dividerColor="#3E3E3E" />}
        <ProductPrice
          data={data}
          loading={skeletonLoading}
          hidden={!displayInfo.includes(DisplayInfo.Price)}
          type={type}
        />
        {/* <Star value={data.rating_summary} size={16} /> */}
      </div>

      {actions && <div onClick={handleAction}>{actions(data)} </div>}
    </div>
  );
};

export default ProductItem;
